import { logException } from '@/helpers/exceptionHelper';
import axios, { AxiosError } from 'axios';
import { isNumber } from 'lodash';

export const handleAxiosError = (error: AxiosError) => {
  if (error && error.code && error.code == 'ATTEMPT_ABORTED') {
    //ignore this error since it was caused by an abort
    return;
  }
  if (axios.isAxiosError(error)) {
    if (error.status === 0) {
      //not connected to internet
      console.info('Exception calling API. Not connected to internet', error);
    } else if (isNumber(error.status) && error.status != 401 && error.status != 403) {
      logException(error, 'API Status Exception: ' + error.status);
    } else {
      //nothing we need to do for 401/403 errors since they are handled by the auth service
    }
  } else {
    logException(error, 'Unhandled API Exception');
  }
};

export function getHelpfulAxiosError(error: any): string {
  // 1) Not an AxiosError?
  if (!axios.isAxiosError(error)) {
    if (error.status) {
      let msg = `HTTP Error (${error.status}).`;
      switch (error.status) {
        case 401:
        case 403:
          msg = 'Not logged in. Please sign out and sign back in again.';
          break;
        case 404:
          msg = 'HTTP Error 404. Your application may be out of date. Please refresh the page.';
          break;
        default:
          msg = `HTTP Error (${error.status}).`;
          if (error.message) {
            msg += ` ${error.message}`;
          }
          break;
      }

      if (error.response?.title) {
        msg += ` (${error.response.title})`;
      }
      return msg;
    }
    return error instanceof Error ? error.message : String(error);
  }

  // 2) It is an AxiosError
  const axiosError = error as AxiosError;
  const code = axiosError.code;
  const response = axiosError.response;
  const data = response?.data;

  // helper to pull a message/detail out of the response body
  const extractDetail = (payload: any): string => {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;
    if (typeof payload === 'object') {
      if ('message' in payload && typeof payload.message === 'string') {
        return payload.message;
      }
      return JSON.stringify(payload);
    }
    return String(payload);
  };

  switch (code) {
    case AxiosError.ETIMEDOUT:
      if (!navigator.onLine) {
        return 'Network timeout. Your internet connection appears to be offline.';
      }
      return 'Network timeout. Please try again later (request timed out).';
    case AxiosError.ECONNABORTED:
      if (!navigator.onLine) {
        return 'Network timeout. Your internet connection appears to be offline.';
      }
      return 'Network timeout. Please try again later (request aborted).';

    case AxiosError.ERR_NETWORK:
      if (!navigator.onLine) {
        return 'Network error. Your internet connection appears to be offline.';
      }
      return 'Network error. Please try again.';

    case AxiosError.ERR_CANCELED:
      return 'Request was canceled.';

    case AxiosError.ERR_INVALID_URL:
      return 'Invalid URL.';

    case AxiosError.ERR_NOT_SUPPORT:
      return 'Request not supported by this client.';

    case AxiosError.ERR_FR_TOO_MANY_REDIRECTS:
      return 'Too many redirects.';

    case AxiosError.ERR_BAD_OPTION:
    case AxiosError.ERR_BAD_OPTION_VALUE:
      return 'Invalid request option.';

    case AxiosError.ERR_DEPRECATED:
      return 'A deprecated option was used in the request.';

    case AxiosError.ERR_BAD_REQUEST:
      // 400-series
      if (response) {
        const detail = extractDetail(data);
        return `Error (${response.status}) – ${response.statusText}` + (detail ? `: ${detail}` : '');
      }
      return 'Bad request – no response from server.';

    case AxiosError.ERR_BAD_RESPONSE:
      // 500-series or malformed response
      return response ? `Server error (${response.status}) – ${response.statusText}` : 'Bad response received from server.';

    default:
      // anything else
      if (response) {
        return `Error (${response.status}) – ${response.statusText}`;
      }
      return axiosError.message;
  }
}

export const handleAxiosErrorWithCallback = (error: AxiosError, additionalCallback: (error: AxiosError) => void) => {
  handleAxiosError(error);
  additionalCallback(error);
};
